import { useState } from 'react'

import { useWindow } from '@krp/client-runtime'
import { InitScreen, shownError, syncInit, syncInitConfirm, type InitResult, type ShownError } from '@krp/sync-ui'

import { queryState, useOperationIds, useSyncSources } from './syncKit'

/**
 * Инициализация связей источника (RP-SYN8, план 3.5). Отметки пар — состояние окна: не сохраняются, пока человек не нажал
 * «Инициализировать». Служба отвечает итогом сразу: сколько связано, сколько уже было и что не связалось — дословно.
 */
export function SyncInitBinding() {
  const { params } = useWindow()
  const resourceId = params.source ?? ''
  const { nameOf } = useSyncSources()
  const { operationFor, settled } = useOperationIds()
  const proposal = syncInit.useQuery({ resourceId }, { skip: resourceId === '' })
  const [confirm] = syncInitConfirm.useMutation()
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<ShownError | null>(null)
  const [result, setResult] = useState<InitResult | null>(null)

  const candidates = proposal.data?.candidates ?? []

  const run = () => {
    const pairs = candidates.filter((candidate) => selected.has(candidate.remoteKey)).map((candidate) => ({ productId: candidate.productId, remoteKey: candidate.remoteKey }))
    const intent = `init:${resourceId}:${pairs.map((pair) => `${pair.remoteKey}=${pair.productId}`).join(',')}`
    setBusy(true)
    setError(null)
    void confirm({ resourceId, operationId: operationFor(intent), pairs })
      .unwrap()
      .then((outcome) => {
        settled(intent)
        setResult(outcome)
        // Связанные пары уходят из кандидатов при перечитывании; отметки сделали своё дело.
        setSelected(new Set())
      })
      .catch((reason: unknown) => setError(shownError('Связи не созданы', reason)))
      .finally(() => setBusy(false))
  }

  const state = queryState(proposal, 'Не удалось найти совпадения')
  return (
    <InitScreen
      vm={{ sourceName: nameOf(resourceId), status: state.status, loadError: state.loadError, proposal: proposal.data ?? null, selected, busy, error, result }}
      actions={{
        toggle: (remoteKey) =>
          setSelected((current) => {
            const next = new Set(current)
            if (next.has(remoteKey)) next.delete(remoteKey)
            else next.add(remoteKey)
            return next
          }),
        selectAll: (all) => setSelected(all ? new Set(candidates.map((candidate) => candidate.remoteKey)) : new Set()),
        confirm: run,
        reload: () => void proposal.refetch(),
      }}
    />
  )
}
